/**
 * What this browser remembers about the Black Friday campaign: the two X dismissals, whether the wins
 * band's CTA was pressed, whether the modal has been shown, and the end of the last free-usage window it
 * saw open.
 *
 * NO runtime imports, every accessor wrapped in try/catch and SSR-safe, same shape as `app-banner.ts`
 * and `piled-up-jobs.ts`. Private mode, blocked storage and the server all read as "nothing remembered",
 * which for every one of these keys is the safe answer: the band shows, and the modal gets another go.
 *
 * The key names are PRODUCT-named (`symphony.*`) and byte-identical with the native apps' `StorageKeys`
 * entries, per the convention `piled-up-jobs.ts` set — the three surfaces answer the same questions, and
 * a name that drifts is a flag one surface silently stops honouring. They deliberately do NOT take the
 * legacy `orion-web:` prefix (see orion-web/CLAUDE.md → "Why Orion still appears").
 */

const OFFER_DISMISSED_KEY = 'symphony.blackFriday.offerDismissed';
const ENDED_DISMISSED_KEY = 'symphony.blackFriday.endedDismissed';
const WINS_OPENED_KEY = 'symphony.blackFriday.winsOpened';
const MODAL_SEEN_KEY = 'symphony.blackFriday.modalSeen';
/**
 * The end of the last free-usage window this browser saw OPEN, as an ISO string.
 *
 * This is the whole reason the two "your 48 free hours are over" bands can exist — the credits overview
 * drops `freeUsageUntil` the moment it passes, so nothing on the wire remembers the window afterwards.
 * See the module note on `black-friday.ts`.
 */
const SEEN_UNTIL_KEY = 'symphony.blackFriday.lastFreeUsageUntil';

function read(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // private mode — the component's in-session state suppresses the repeat for this visit
  }
}

function clear(key: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // nothing to do; a key that cannot be removed was never written either
  }
}

export function blackFridayOfferDismissed(): boolean {
  return read(OFFER_DISMISSED_KEY) === '1';
}

export function markBlackFridayOfferDismissed(): void {
  write(OFFER_DISMISSED_KEY, '1');
}

export function blackFridayEndedDismissed(): boolean {
  return read(ENDED_DISMISSED_KEY) === '1';
}

export function markBlackFridayEndedDismissed(): void {
  write(ENDED_DISMISSED_KEY, '1');
}

export function blackFridayWinsOpened(): boolean {
  return read(WINS_OPENED_KEY) === '1';
}

export function markBlackFridayWinsOpened(): void {
  write(WINS_OPENED_KEY, '1');
}

export function blackFridayModalSeen(): boolean {
  return read(MODAL_SEEN_KEY) === '1';
}

export function markBlackFridayModalSeen(): void {
  write(MODAL_SEEN_KEY, '1');
}

/** The end of the last window this browser watched, or null when it has never seen one. */
export function blackFridaySeenUntil(): string | null {
  const raw = read(SEEN_UNTIL_KEY);
  if (!raw) return null;
  return Number.isFinite(new Date(raw).getTime()) ? raw : null;
}

/**
 * Remember an open window's end. Called whenever the credits overview reports one.
 *
 * Keeps the LATER of the two, because free hours from several grants add up and the overview can report
 * a window that now ends further out than the one already remembered. Never moves backwards, so a stale
 * read cannot shorten a window the user is still inside.
 */
export function rememberBlackFridayUntil(iso: string): void {
  const next = new Date(iso).getTime();
  if (!Number.isFinite(next)) return;
  const current = blackFridaySeenUntil();
  if (current && new Date(current).getTime() >= next) return;
  write(SEEN_UNTIL_KEY, iso);
}

/** Same-tab writes fire no `storage` event, so the mounted band and modal listen for this instead. */
export const BLACK_FRIDAY_RESET_EVENT = 'symphony:black-friday-reset';

/**
 * Forget everything this browser remembers about the campaign. Run on logout: every key here is what
 * the signed-in ACCOUNT has been shown, so the next account must start clean, as on the phones.
 */
export function clearBlackFridayState(): void {
  [OFFER_DISMISSED_KEY, ENDED_DISMISSED_KEY, WINS_OPENED_KEY, MODAL_SEEN_KEY, SEEN_UNTIL_KEY].forEach(clear);
}

/** Dev Tools ▸ Black Friday: put this browser back to "never seen the campaign". */
export function resetBlackFridayState(): void {
  clearBlackFridayState();
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(BLACK_FRIDAY_RESET_EVENT));
}
